<template>
  <div>
    <b-row class="pt-2 pb-1 align-items-center">
      <b-col cols="12" class="d-flex align-items-center justify-content-between">
        <BreadcrumbMenu />
        <b-badge v-if="isEditMode && currentItem" variant="light-primary" class="text-capitalize">
          {{ $t('policy_exception.status') }}:
          <StatusBadge :status="currentItem && currentItem.status" />
        </b-badge>
      </b-col>
    </b-row>

    <b-row class="pb-1">
      <b-col cols="12" class="d-flex align-items-center ml-2 mb-2">
        <b-button variant="outline-secondary" size="sm" class="rounded-circle p-25" @click="goBack">
          <feather-icon icon="ArrowLeftIcon" size="16" />
        </b-button>
        <span class="ml-1 text-capitalize text-dark cursor-pointer goback-text" @click="goBack">{{ $t('actions.go_back') }}</span>
      </b-col>
    </b-row>

    <div v-if="loading" class="policy-exception-loading-overlay">
      <div class="d-flex flex-column align-items-center justify-content-center">
        <b-spinner variant="primary" />
        <p class="mt-2 mb-0 text-primary">{{ $t('actions.loading') }}</p>
      </div>
    </div>
    <b-card v-else-if="isEditMode && loadError" class="m-2">
      <p class="text-danger mb-0">{{ $t('policy_exception.unable_to_load') }}</p>
      <b-button variant="outline-secondary" size="sm" class="mt-2" @click="goBack">{{ $t('actions.cancel') }}</b-button>
    </b-card>
    <b-card v-else class="m-2">
      <validation-observer ref="refFormObserver">
        <b-form @submit.prevent="onSubmit">
          <fieldset :disabled="!canEditFormItem">
          <b-row>
            <b-col cols="12">
              <validation-provider v-slot="{ errors }" name="reference_number" rules="required">
                <b-form-group :label="$t('policy_exception.reference_number')" label-for="reference_number">
                  <b-form-input
                    id="reference_number"
                    v-model="formData.reference_number"
                    :placeholder="'EP-' + String(new Date().getFullYear()).slice(-2) + '-000000'"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="scope_type_id" rules="required">
                <b-form-group :label="$t('policy_exception.scope_type')" label-for="scope_type_id">
                  <v-select
                    id="scope_type_id"
                    v-model="formData.scope_type_id"
                    :options="scopeTypeOptions"
                    label="name"
                    :reduce="(item) => item.id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_scope_type')"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="6">
              <template v-if="showUnitsSelector">
                <validation-provider v-slot="{ errors }" name="unit_ids" :rules="showUnitsSelector ? 'required' : ''">
                  <b-form-group :label="$t('policy_exception.units')" label-for="unit_ids">
                    <UnitsSelectPaginated
                      id="unit_ids"
                      class="units-select"
                      v-model="formData.unit_ids"
                      :placeholder="$t('policy_exception.select_units')"
                      :disabled="!canEditFormItem"
                      :initial-options="unitInitialOptions"
                      multiple
                    />
                    <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </template>
              <template v-else-if="showSectorsSelector">
                <validation-provider v-slot="{ errors }" name="sector_ids" :rules="showSectorsSelector ? 'required' : ''">
                  <b-form-group :label="$t('policy_exception.sectors')" label-for="sector_ids">
                    <SectorsSelectPaginated
                      id="sector_ids"
                      class="sectors-select"
                      v-model="formData.sector_ids"
                      :placeholder="$t('policy_exception.select_sectors')"
                      :disabled="!canEditFormItem"
                      :initial-options="sectorInitialOptions"
                      multiple
                    />
                    <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </template>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="request_type_id" rules="required">
                <b-form-group :label="$t('policy_exception.request_type')" label-for="request_type_id">
                  <v-select
                    id="request_type_id"
                    v-model="formData.request_type_id"
                    :options="requestTypeOptions"
                    label="name"
                    :reduce="(item) => item.id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_request_type')"
                    :disabled="isRequestTypeLocked || !canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="6" class="d-flex align-items-end">
              <b-button
                variant="outline-primary"
                size="sm"
                class="mb-1"
                @click="showRiskTableModal = true"
              >
                {{ $t('policy_exception.show_risk_table') }}
              </b-button>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="exception_type_id" rules="required">
                <b-form-group :label="$t('policy_exception.exception_type')" label-for="exception_type_id">
                  <v-select
                    id="exception_type_id"
                    v-model="formData.exception_type_id"
                    :options="exceptionTypeOptions"
                    label="name"
                    :reduce="(item) => item.id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_exception_type')"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col v-if="showStandardsAffected" cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="standard_ids" :rules="showStandardsAffected ? 'required' : ''">
                <b-form-group :label="$t('policy_exception.standards_affected')" label-for="standard_ids">
                  <v-select
                    id="standard_ids"
                    class="standards-select"
                    v-model="formData.standard_ids"
                    :options="standardOptions"
                    label="label"
                    :reduce="(item) => item.id"
                    :filter="filterStandardByNameOrCode"
                    multiple
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_standards')"
                    :disabled="!canEditFormItem"
                  >
                    <template #no-options>
                      <span />
                    </template>
                  </v-select>
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row v-if="isSectionDetailEnabled">
            <b-col cols="12">
              <validation-provider v-slot="{ errors }" name="section_detail" :rules="isSectionDetailEnabled ? 'required' : ''">
                <b-form-group :label="$t('policy_exception.section_detail')" label-for="section_detail">
                  <b-form-textarea
                    id="section_detail"
                    v-model="formData.section_detail"
                    rows="3"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row v-if="isSectionDetailEnabled">
            <b-col cols="12">
              <b-form-group :label="$t('policy_exception.section_attachments')" label-for="section_attachments">
                <div
                  class="file-upload-info-box attachments-info mb-2"
                  v-b-tooltip.hover.html
                  :title="$t('actions.file_upload.tooltip')"
                >
                  <feather-icon icon="InfoIcon" size="14" class="info-icon" />
                  <small class="file-upload-info-text">
                    <strong>{{ $t('actions.file_upload.accepted_types_label') }}</strong>
                    {{ $t('policy_exception.attachments_accepted_types') }}
                    <strong>{{ $t('actions.file_upload.max_label') }}</strong>
                    {{ $t('actions.file_upload.max_per_document') }}
                    -
                    {{ $t('actions.file_upload.max_documents_total') }}
                  </small>
                </div>
                <div v-if="canEditFormItem" class="d-flex align-items-center flex-wrap gap-2 mb-2">
                  <b-button
                    variant="primary"
                    size="sm"
                    :disabled="formData.section_attachments && formData.section_attachments.length >= 5"
                    class="attachments-upload-btn"
                  >
                    <feather-icon icon="UploadIcon" size="18" />
                    <input
                      ref="sectionAttachmentsInputRef"
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.csv,.ods,.zip"
                      class="file-input-hidden"
                      @change="handleSectionAttachmentChange"
                    />
                  </b-button>
                </div>
                <ul v-if="formData.section_attachments && formData.section_attachments.length" class="attachments-list list-unstyled mt-2 mb-0">
                  <li
                    v-for="(att, index) in formData.section_attachments"
                    :key="'section-' + index"
                    class="attachment-item d-flex align-items-center justify-content-between py-2 px-2 mb-1 rounded border"
                  >
                    <span
                      class="attachment-preview flex-grow-1 text-truncate cursor-pointer"
                      @click="onSectionAttachmentClick(att)"
                    >
                      <feather-icon icon="FileTextIcon" size="16" class="mr-1 align-middle" />
                      {{ att.name || '-' }}
                      <small v-if="att.raw" class="text-muted ml-1">({{ $t('policy_exception.pending_save') }})</small>
                    </span>
                    <b-button
                      v-if="canEditFormItem"
                      variant="danger"
                      size="sm"
                      class="btn-icon btn-sm ml-1"
                      @click.stop="removeSectionAttachment(index)"
                    >
                      <feather-icon icon="XIcon" size="14" />
                    </b-button>
                  </li>
                </ul>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="residual_risk_id" rules="required">
                <b-form-group :label="$t('policy_exception.residual_risk')" label-for="residual_risk_id">
                  <v-select
                    id="residual_risk_id"
                    v-model="formData.residual_risk_id"
                    :options="residualRiskOptions"
                    label="name"
                    :reduce="(item) => item.id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_residual_risk')"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="inherent_risk_id" rules="required">
                <b-form-group :label="$t('policy_exception.inherent_risk')" label-for="inherent_risk_id">
                  <v-select
                    id="inherent_risk_id"
                    v-model="formData.inherent_risk_id"
                    :options="inherentRiskOptions"
                    label="name"
                    :reduce="(item) => item.id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_inherent_risk')"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="source_id" rules="required">
                <b-form-group :label="$t('policy_exception.source')" label-for="source_id">
                  <v-select
                    id="source_id"
                    v-model="formData.source_id"
                    :options="sourceOptions"
                    label="name"
                    :reduce="(item) => item.id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_source')"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="4">
              <validation-provider v-slot="{ errors }" name="request_date" rules="required">
                <b-form-group :label="$t('policy_exception.request_date')" label-for="request_date">
                  <flat-pickr
                    id="request_date"
                    v-model="formData.request_date"
                    class="form-control"
                    :config="requestDateConfig"
                    :disabled="isRequestDateLocked || !canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="4">
              <validation-provider v-slot="{ errors }" name="start_date" rules="required">
                <b-form-group :label="$t('policy_exception.start_date')" label-for="start_date">
                  <flat-pickr
                    id="start_date"
                    v-model="formData.start_date"
                    class="form-control"
                    :config="startDateConfig"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="4">
              <validation-provider v-slot="{ errors }" name="end_date" rules="required">
                <b-form-group :label="$t('policy_exception.end_date')" label-for="end_date">
                  <flat-pickr
                    id="end_date"
                    v-model="formData.end_date"
                    class="form-control"
                    :config="endDateConfig"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="4">
              <b-form-group :label="$t('policy_exception.regulatory_required')">
                <b-form-checkbox v-model="formData.regulatory_required" switch :disabled="!canEditFormItem" />
              </b-form-group>
            </b-col>
            <b-col v-if="showRegulationDescription" cols="12" md="8">
              <validation-provider
                v-slot="{ errors }"
                name="regulation_description"
                :rules="formData.regulatory_required ? 'required' : ''"
              >
                <b-form-group :label="$t('policy_exception.regulation_description')" label-for="regulation_description">
                  <b-form-textarea
                    id="regulation_description"
                    v-model="formData.regulation_description"
                    rows="2"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <validation-provider v-slot="{ errors }" name="description" rules="required">
                <b-form-group :label="$t('policy_exception.description')" label-for="description">
                  <b-form-textarea
                    id="description"
                    v-model="formData.description"
                    rows="3"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <validation-provider v-slot="{ errors }" name="compensatory_measures" rules="required">
                <b-form-group :label="$t('policy_exception.compensatory_measures')" label-for="compensatory_measures">
                  <b-form-textarea
                    id="compensatory_measures"
                    v-model="formData.compensatory_measures"
                    rows="3"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <validation-provider v-slot="{ errors }" name="justification" rules="required">
                <b-form-group :label="$t('policy_exception.justification')" label-for="justification">
                  <b-form-textarea
                    id="justification"
                    v-model="formData.justification"
                    rows="3"
                    :disabled="!canEditFormItem"
                  />
                  <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-form-group :label="$t('policy_exception.attachments')" label-for="attachments">
                <div
                  class="file-upload-info-box attachments-info mb-2"
                  v-b-tooltip.hover.html
                  :title="$t('actions.file_upload.tooltip')"
                >
                  <feather-icon icon="InfoIcon" size="14" class="info-icon" />
                  <small class="file-upload-info-text">
                    <strong>{{ $t('actions.file_upload.accepted_types_label') }}</strong>
                    {{ $t('policy_exception.attachments_accepted_types') }}
                    <strong>{{ $t('actions.file_upload.max_label') }}</strong>
                    {{ $t('actions.file_upload.max_per_document') }}
                    -
                    {{ $t('actions.file_upload.max_documents_total') }}
                  </small>
                </div>
                <div v-if="canEditFormItem" class="d-flex align-items-center flex-wrap gap-2 mb-2">
                  <b-button
                    variant="primary"
                    size="sm"
                    :disabled="formData.attachments && formData.attachments.length >= 5"
                    class="attachments-upload-btn"
                  >
                    <feather-icon icon="UploadIcon" size="18" />
                    <input
                      ref="attachmentsInputRef"
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.csv,.ods,.zip"
                      class="file-input-hidden"
                      @change="handleAttachmentChange"
                    />
                  </b-button>
                </div>
                <ul v-if="formData.attachments && formData.attachments.length" class="attachments-list list-unstyled mt-2 mb-0">
                  <li
                    v-for="(att, index) in formData.attachments"
                    :key="index"
                    class="attachment-item d-flex align-items-center justify-content-between py-2 px-2 mb-1 rounded border"
                  >
                    <span
                      class="attachment-preview flex-grow-1 text-truncate cursor-pointer"
                      @click="onAttachmentClick(att)"
                    >
                      <feather-icon icon="FileTextIcon" size="16" class="mr-1 align-middle" />
                      {{ att.name || '-' }}
                      <small v-if="att.raw" class="text-muted ml-1">({{ $t('policy_exception.pending_save') }})</small>
                    </span>
                    <b-button
                      v-if="canEditFormItem"
                      variant="danger"
                      size="sm"
                      class="btn-icon btn-sm ml-1"
                      @click.stop="removeAttachment(index)"
                    >
                      <feather-icon icon="XIcon" size="14" />
                    </b-button>
                  </li>
                </ul>
              </b-form-group>
            </b-col>
          </b-row>
          </fieldset>

          <b-modal
            v-model="showRiskTableModal"
            :title="$t('policy_exception.risk_table_title')"
            hide-footer
            body-class="p-0"
          >
            <div class="risk-level-card p-3">
              <div class="risk-level-table">
                <div class="risk-level-row risk-level-row--head">
                  <div class="risk-level-col-level">{{ $t('policy_exception.risk_level_table_level') }}</div>
                  <div class="risk-level-col-desc">{{ $t('policy_exception.risk_level_table_description') }}</div>
                </div>
                <div
                  v-for="(row, index) in riskLevelTableData"
                  :key="index"
                  class="risk-level-row"
                >
                  <div class="risk-level-col-level">{{ row.nivel }}</div>
                  <div class="risk-level-col-desc risk-level-desc">{{ row.descripcion }}</div>
                </div>
              </div>
            </div>
          </b-modal>

          <b-row v-if="isEditMode" class="mt-3">
            <b-col cols="12">
              <div class="history-card">
                <div class="history-header d-flex align-items-center justify-content-between">
                  <strong>{{ $t('policy_exception.history') }}</strong>
                  <b-badge variant="light-primary">
                    {{ $t('policy_exception.status') }}:
                    <StatusBadge :status="currentItem && currentItem.status" />
                  </b-badge>
                </div>
                <div v-if="sortedStatusHistory.length" class="history-table">
                  <div class="history-row history-row--head" :style="historyGridStyle">
                    <div class="col-state text-body">{{ $t('policy_exception.history_state') }}</div>
                    <div class="col-user text-body">{{ $t('policy_exception.history_user') }}</div>
                    <div class="col-date text-body">{{ $t('policy_exception.history_date') }}</div>
                  </div>
                  <div
                    v-for="entry in sortedStatusHistory"
                    :key="entry.id"
                    class="history-row"
                    :style="historyGridStyle"
                  >
                    <div class="col-state">
                      <StatusBadge :status="{ name: getStatusNameFromEntry(entry) }" />
                    </div>
                    <div class="col-user">
                      <span class="history-user">
                        {{ getUserNameFromEntry(entry) || getUserEmailFromEntry(entry) || '-' }}
                      </span>
                    </div>
                    <div class="col-date">
                      <small class="history-date">{{ formatHistoryDate(getHistoryDate(entry)) }}</small>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center text-muted py-3">{{ $t('no_record_found') }}</div>
              </div>
            </b-col>
          </b-row>

          <b-row v-if="isEditMode" class="mt-2">
            <b-col cols="12">
              <b-form-group :label="$t('policy_exception.comments')" label-for="form-comments">
                <b-form-textarea
                  id="form-comments"
                  v-model="formData.comments"
                  rows="3"
                  :placeholder="$t('policy_exception.comments_placeholder')"
                  :disabled="!canEditFormItem"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-if="isEditMode && !(showReviewActions && canReviewItem)" class="mt-2">
            <b-col cols="12" class="d-flex flex-wrap align-items-center justify-content-center">
              <template v-if="canSendToReviewItem && !showReviewActions">
                <v-select
                  v-model="selectedReviewer"
                  :options="userOptions"
                  :reduce="(u) => u.value"
                  :clearable="true"
                  :disabled="workflowLoading"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="$t('policy_exception.select_reviewer')"
                  class="reviewer-select mr-1 mb-1"
                  style="min-width: 600px"
                />
                <b-button
                  variant="warning"
                  size="sm"
                  class="mr-2 mb-1"
                  :disabled="workflowLoading || !selectedReviewer || !statusAllowsSendToReview"
                  @click="runSaveThenWorkflow(onSendToReview)"
                >
                  {{ $t('policy_exception.btn_send_to_review') }}
                </b-button>
              </template>
              <template v-if="showSpecialButtonsBlockItem">
                <div class="d-flex align-items-center">
                  <v-select
                    v-model="selectedReassign"
                    :options="reassignableOptions"
                    :reduce="(u) => u.value"
                    :clearable="true"
                    :disabled="workflowLoading"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :placeholder="$t('policy_exception.select_user')"
                    class="reassign-select mr-1 mb-1 policy-exception-special-select"
                    style="min-width: 600px"
                  />
                  <b-button
                    variant="warning"
                    size="sm"
                    class="mr-2 mb-1"
                    :disabled="workflowLoading || !selectedReassign"
                    @click="runSaveThenWorkflow(onReassign)"
                  >
                    {{ $t('policy_exception.workflow.reassign') }}
                  </b-button>
                </div>
              </template>
              <template v-else-if="(canAssignNewUser || isCurrentUserTheRejectorItem) && canReassignAfterRejectedItem">
                <v-select
                  v-model="selectedReassign"
                  :options="reassignableOptions"
                  :reduce="(u) => u.value"
                  :clearable="true"
                  :disabled="workflowLoading"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="$t('policy_exception.select_user')"
                  class="reassign-select mr-1 mb-1"
                  style="min-width: 600px"
                />
                <b-button
                  variant="warning"
                  size="sm"
                  class="mr-2 mb-1"
                  :disabled="workflowLoading || !selectedReassign"
                  @click="runSaveThenWorkflow(onReassign)"
                >
                  {{ $t('policy_exception.workflow.reassign') }}
                </b-button>
              </template>
              <template v-else-if="showReassignAsNonAssigneeItem">
                <v-select
                  v-if="isReassignInReviewMode"
                  v-model="selectedReviewer"
                  :options="userOptions"
                  :reduce="(u) => u.value"
                  :clearable="true"
                  :disabled="workflowLoading"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="$t('policy_exception.select_reviewer')"
                  class="reassign-select mr-1 mb-1"
                  style="min-width: 600px"
                />
                <v-select
                  v-else
                  v-model="selectedReassign"
                  :options="reassignableOptions"
                  :reduce="(u) => u.value"
                  :clearable="true"
                  :disabled="workflowLoading"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="$t('policy_exception.select_user')"
                  class="reassign-select mr-1 mb-1"
                  style="min-width: 600px"
                />
                <b-button
                  variant="warning"
                  size="sm"
                  class="mr-2 mb-1"
                  :disabled="workflowLoading || (isReassignInReviewMode ? !selectedReviewer : !selectedReassign)"
                  @click="runSaveThenWorkflow(() => isReassignInReviewMode ? onSendToReview() : onReassign())"
                >
                  {{ $t('policy_exception.workflow.reassign') }}
                </b-button>
              </template>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" class="d-flex flex-wrap align-items-center justify-content-center mt-2">
              <template v-if="showReviewActions && canReviewItem">
                <b-button
                  variant="success"
                  class="mr-2"
                  :disabled="workflowLoading"
                  @click="runSaveThenWorkflow(() => onWorkflowAction('approved'))"
                >
                  {{ $t('actions.approve') }}
                </b-button>
                <b-button
                  variant="danger"
                  class="mr-2"
                  :disabled="workflowLoading"
                  @click="runSaveThenWorkflow(() => onWorkflowAction('rejected'))"
                >
                  {{ $t('actions.reject') }}
                </b-button>
              </template>
              <template v-if="canEditFormItem">
                <b-button
                  type="submit"
                  variant="primary"
                  :disabled="loading || isSubmitting"
                  class="mr-2"
                >
                  {{ $t('actions.save') }}
                </b-button>
              </template>
              <b-button variant="outline-secondary" @click="goBack">
                {{ $t('actions.cancel') }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, BFormInvalidFeedback, BModal } from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { ref, computed, onMounted, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import usePolicyExceptionForm, { resolveInProgressStatusId, getFieldLabelKey } from './usePolicyExceptionForm'
import {
  getStatusName,
  canSendToReview,
  canReview,
  canReassignAfterRejected,
  isCurrentUserTheRejector,
  canShowReassignAsNonAssignee,
  canShowSpecialButtonsBlock,
  canEditForm,
  showReviewActions as showReviewActionsFn,
  fetchReviewerOptions,
  fetchReassignableOptions,
  getStatusHistoryEntries,
  getHistoryDate,
  getStatusNameFromEntry,
  getUserNameFromEntry,
  getUserEmailFromEntry,
} from './useWorkflow'
import store from '@/store'
import i18n from '@/libs/i18n'
import BreadcrumbMenu from '@/components/BreadcrumbMenu.vue'
import StatusBadge from './components/StatusBadge.vue'
import UnitsSelectPaginated from '@/components/UnitsSelectPaginated.vue'
import SectorsSelectPaginated from '@/components/SectorsSelectPaginated.vue'

const t = (key, ...args) => i18n.t(key, ...args)

function formatUnitOptionLabel(description, sirh, rems) {
  const desc = description != null && description !== '' ? String(description) : ''
  const sirhDisplay = sirh != null && sirh !== '' ? String(sirh) : '-'
  const remsDisplay = rems != null && rems !== '' ? String(rems) : '-'
  const suffix = `SIRH: ${sirhDisplay} | REMS: ${remsDisplay}`
  return desc ? `${desc} | ${suffix}` : suffix
}

export default {
  components: {
    BCard,
    BRow,
    BCol,
    StatusBadge,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BFormInvalidFeedback,
    BModal,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    flatPickr,
    BreadcrumbMenu,
    UnitsSelectPaginated,
    SectorsSelectPaginated,
  },
  setup(props, { root }) {
    const toast = useToast()
    const refFormObserver = ref(null)
    const isSubmitting = ref(false)
    const dateValidationErrors = ref([])
    const catalogsRef = ref({})

    const currentRoute = computed(() => (root && root.$route) || { params: {} })
    const isEditMode = computed(() => !!(currentRoute.value && currentRoute.value.params && currentRoute.value.params.id))
    const loading = ref(false)
    const loadError = ref(false)

    const {
      formData,
      unitOptions,
      sectorOptions,
      showUnitsSelector,
      showSectorsSelector,
      showRegulationDescription,
      isSectionDetailEnabled,
      showStandardsAffected,
      validateDates,
      validateBeforeReview,
      validateForSave,
      resetForm,
    } = usePolicyExceptionForm({}, catalogsRef)

    const currentItem = computed(() => store.getters['policyException/getCurrent'])
    const unitInitialOptions = computed(() => {
      const item = currentItem.value
      if (!item || !Array.isArray(item.units) || item.units.length === 0) return []
      return item.units.map((u) => {
        const id = u && (u.id != null ? u.id : u.unit && u.unit.id)
        if (id == null) return null
        const desc = u.description ?? (u.unit && u.unit.description)
        const sirh = u.sirh ?? (u.unit && u.unit.sirh)
        const rems = u.rems ?? (u.unit && u.unit.rems)
        return { value: id, label: formatUnitOptionLabel(desc, sirh, rems) }
      }).filter(Boolean)
    })
    const sectorInitialOptions = computed(() => {
      const item = currentItem.value
      if (!item || !Array.isArray(item.sectors) || item.sectors.length === 0) return []
      return item.sectors.map((s) => {
        const id = s && (s.id != null ? s.id : s.sector && s.sector.id)
        if (id == null) return null
        const name = s.name ?? (s.sector && s.sector.name)
        return { value: id, label: name != null ? String(name) : String(id) }
      }).filter(Boolean)
    })
    const selectedReviewer = ref(null)
    const selectedReassign = ref(null)
    const userOptions = ref([])
    const reassignableOptions = ref([])
    const workflowLoading = ref(false)
    const attachmentsInputRef = ref(null)
    const sectionAttachmentsInputRef = ref(null)
    const showRiskTableModal = ref(false)

    const MAX_ATTACHMENT_SIZE_MB = 5
    const MAX_ATTACHMENTS = 5

    const formatConflictUnitLabel = (description, sirh, rems) => {
      const desc = description != null && description !== '' ? String(description) : ''
      const sirhDisplay = sirh != null && sirh !== '' ? String(sirh) : '-'
      const remsDisplay = rems != null && rems !== '' ? String(rems) : '-'
      const suffix = `SIRH: ${sirhDisplay} | REMS: ${remsDisplay}`
      return desc ? `${desc} | ${suffix}` : suffix
    }

    const buildConflictInmueblesMessage = (conflictingPolicies) => {
      const lines = []
      const policies = Array.isArray(conflictingPolicies) ? conflictingPolicies : []
      const template = t('policy_exception.conflict_inmueble_in_policy')
      policies.forEach((policy) => {
        const referenceNumber = policy.reference_number != null ? String(policy.reference_number) : '-'
        const exceptionTypeName = policy.exception_type_name != null ? String(policy.exception_type_name).replace(/_/g, ' ') : '-'
        const units = Array.isArray(policy.units) ? policy.units : []
        units.forEach((unit) => {
          const unitLabel = formatConflictUnitLabel(unit.description, unit.sirh, unit.rems)
          lines.push(
            template
              .replace('{unitLabel}', unitLabel)
              .replace('{referenceNumber}', referenceNumber)
              .replace('{exceptionTypeName}', exceptionTypeName)
          )
        })
      })
      return lines.join(' ')
    }

    const resolveIdTenant = () => {
      const item = currentItem.value
      if (item && item.units && item.units[0] && item.units[0].router && item.units[0].router.id_tenant != null) {
        return item.units[0].router.id_tenant
      }
      const storageTenants = store.getters['tenants_storage/getItems'] || []
      if (Array.isArray(storageTenants) && storageTenants.length && storageTenants[0]?.id != null) {
        return storageTenants[0].id
      }
      const tenants = store.getters['user/getTenants'] || store.getters['tenant/getSelectedItems'] || []
      const first = Array.isArray(tenants) ? tenants[0] : null
      return first && (first.id != null || first.value != null) ? (first.id ?? first.value) : null
    }

    const buildFormDataFromPayload = (payload, newFiles = [], newSectionFiles = []) => {
      const fd = new FormData()
      const existingAttachments = payload.attachments
      const existingSectionAttachments = payload.section_attachments
      const payloadWithoutFiles = { ...payload }
      delete payloadWithoutFiles.attachments
      delete payloadWithoutFiles.section_attachments
      Object.keys(payloadWithoutFiles).forEach((key) => {
        const val = payloadWithoutFiles[key]
        if (val === undefined || val === null) return
        if (key === 'comments') {
          fd.append(key, String(val ?? ''))
          return
        }
        if (Array.isArray(val)) {
          val.forEach((v) => fd.append(`${key}[]`, v))
        } else if (typeof val === 'boolean') {
          fd.append(key, val ? '1' : '0')
        } else if (typeof val === 'object') {
          fd.append(key, JSON.stringify(val))
        } else {
          fd.append(key, String(val))
        }
      })
      const idTenant = resolveIdTenant()
      if (idTenant) fd.append('id_tenant', idTenant)
      if (Array.isArray(existingAttachments) && existingAttachments.length) {
        fd.append('attachments', JSON.stringify(existingAttachments))
      }
      newFiles.forEach((file) => {
        if (file instanceof File) fd.append('attachments[]', file)
      })
      if (Array.isArray(existingSectionAttachments) && existingSectionAttachments.length) {
        fd.append('section_attachments', JSON.stringify(existingSectionAttachments))
      }
      newSectionFiles.forEach((file) => {
        if (file instanceof File) fd.append('section_attachments[]', file)
      })
      return fd
    }

    const handleAttachmentChange = (event) => {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      const currentCount = (formData.value.attachments || []).length
      if (currentCount >= MAX_ATTACHMENTS) {
        toast({
          component: ToastificationContent,
          props: {
            title: t('actions.file_upload.max_documents_error', { n: MAX_ATTACHMENTS }),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }
      const maxBytes = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
      const validFiles = []
      for (const file of files) {
        if (file.size > maxBytes) {
          toast({
            component: ToastificationContent,
            props: {
              title: t('actions.file_upload.max_size_error', { name: file.name, n: MAX_ATTACHMENT_SIZE_MB }),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          continue
        }
        if (currentCount + validFiles.length >= MAX_ATTACHMENTS) {
          toast({
            component: ToastificationContent,
            props: {
              title: t('actions.file_upload.max_documents_error', { n: MAX_ATTACHMENTS }),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          break
        }
        validFiles.push(file)
      }
      if (validFiles.length === 0) return
      const list = [...(formData.value.attachments || [])]
      validFiles.forEach((file) => list.push({ raw: file, name: file.name || '' }))
      formData.value.attachments = list
    }

    const removeAttachment = (index) => {
      const list = [...(formData.value.attachments || [])]
      list.splice(index, 1)
      formData.value.attachments = list
    }

    const handleSectionAttachmentChange = (event) => {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      const currentCount = (formData.value.section_attachments || []).length
      if (currentCount >= MAX_ATTACHMENTS) {
        toast({
          component: ToastificationContent,
          props: {
            title: t('actions.file_upload.max_documents_error', { n: MAX_ATTACHMENTS }),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }
      const maxBytes = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
      const validFiles = []
      for (const file of files) {
        if (file.size > maxBytes) {
          toast({
            component: ToastificationContent,
            props: {
              title: t('actions.file_upload.max_size_error', { name: file.name, n: MAX_ATTACHMENT_SIZE_MB }),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          continue
        }
        if (currentCount + validFiles.length >= MAX_ATTACHMENTS) {
          toast({
            component: ToastificationContent,
            props: {
              title: t('actions.file_upload.max_documents_error', { n: MAX_ATTACHMENTS }),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          break
        }
        validFiles.push(file)
      }
      if (validFiles.length === 0) return
      const list = [...(formData.value.section_attachments || [])]
      validFiles.forEach((file) => list.push({ raw: file, name: file.name || '' }))
      formData.value.section_attachments = list
    }

    const removeSectionAttachment = (index) => {
      const list = [...(formData.value.section_attachments || [])]
      list.splice(index, 1)
      formData.value.section_attachments = list
    }

    const onSectionAttachmentClick = (att) => {
      if (att.url) {
        window.open(att.url, '_blank', 'noopener,noreferrer')
        return
      }
      if (att.raw || !att.url) {
        toast({
          component: ToastificationContent,
          props: {
            title: t('policy_exception.save_to_view_files'),
            icon: 'InfoIcon',
            variant: 'info',
          },
        })
      }
    }

    const hasPendingAttachmentChanges = computed(() =>
      (formData.value.attachments || []).some((a) => a.raw && a.raw instanceof File)
    )

    const onAttachmentClick = (att) => {
      if (att.url) {
        window.open(att.url, '_blank', 'noopener,noreferrer')
        return
      }
      if (att.raw || !att.url) {
        toast({
          component: ToastificationContent,
          props: {
            title: t('policy_exception.save_to_view_files'),
            icon: 'InfoIcon',
            variant: 'info',
          },
        })
      }
    }

    const getCan = () => (root && typeof root.$can === 'function' ? root.$can : null)
    const canEditFormItem = computed(() => {
      if (!isEditMode.value) return true
      return canEditForm(currentItem.value, getCan())
    })
    const canSendToReviewItem = computed(() => canSendToReview(currentItem.value))
    const canReviewItem = computed(() => canReview(currentItem.value, getCan()))
    const canReassignAfterRejectedItem = computed(() => canReassignAfterRejected(currentItem.value, getCan()))
    const isCurrentUserTheRejectorItem = computed(() => isCurrentUserTheRejector(currentItem.value))
    const showReassignAsNonAssigneeItem = computed(() => canShowReassignAsNonAssignee(currentItem.value, getCan()))
    const showSpecialButtonsBlockItem = computed(() => canShowSpecialButtonsBlock(currentItem.value, getCan()))
    const statusAllowsSendToReview = computed(() => {
      const name = getStatusName(currentItem.value)
      return name === 'in_progress' || name === 'reassigned'
    })
    const isReassignInReviewMode = computed(() => getStatusName(currentItem.value) === 'in_review')
    const showReviewActions = computed(() => showReviewActionsFn(currentItem.value))
    const canAssignNewUser = computed(() => {
      const $can = getCan()
      return $can && $can('special_permission_assign_new_user', 'PolicyException')
    })

    const statusHistoryEntries = computed(() => getStatusHistoryEntries(currentItem.value))
    const sortedStatusHistory = computed(() =>
      [...statusHistoryEntries.value].sort(
        (a, b) => new Date(getHistoryDate(b) || 0) - new Date(getHistoryDate(a) || 0)
      )
    )
    const historyGridStyle = {
      gridTemplateColumns: 'max-content minmax(140px, 1fr) minmax(190px, max-content)',
    }
    const riskLevelTableData = computed(() => [
        {
          nivel: t('policy_exception.risk_level_critical'),
          descripcion: t('policy_exception.risk_level_critical_desc'),
        },
        {
          nivel: t('policy_exception.risk_level_high'),
          descripcion: t('policy_exception.risk_level_high_desc'),
        },
        {
          nivel: t('policy_exception.risk_level_medium'),
          descripcion: t('policy_exception.risk_level_medium_desc'),
        },
        {
          nivel: t('policy_exception.risk_level_low'),
          descripcion: t('policy_exception.risk_level_low_desc'),
        },
      ])
    const formatHistoryDate = (val) => {
      if (!val) return '-'
      const d = new Date(val)
      return isNaN(d.getTime()) ? val : d.toLocaleString()
    }

    const loadInitialEditData = async () => {
      const id = currentRoute.value.params.id
      if (!id) return
      const [catalogsPayload, standards, data, reviewers, reassignable] = await Promise.all([
        store.dispatch('policyException/fetchCatalogs'),
        store.dispatch('policyException/fetchStandards'),
        store.dispatch('policyException/get', id),
        fetchReviewerOptions(),
        fetchReassignableOptions(),
      ])
      catalogsRef.value = {
        ...(catalogsPayload || store.getters['policyException/getCatalogs'] || {}),
        standards: Array.isArray(standards) && standards.length ? standards : (catalogsPayload?.standards || []),
      }
      if (data) {
        resetForm(data)
      } else {
        loadError.value = true
      }
      userOptions.value = Array.isArray(reviewers) ? reviewers : []
      reassignableOptions.value = Array.isArray(reassignable) ? reassignable : []
    }

    const loadEditData = async () => {
      const id = currentRoute.value.params.id
      if (!id) return
      loading.value = true
      loadError.value = false
      try {
        await loadInitialEditData()
      } catch (_) {
        loadError.value = true
      } finally {
        loading.value = false
      }
    }

    const refetchCurrentItem = async () => {
      const id = currentRoute.value.params.id
      if (!id) return
      try {
        const data = await store.dispatch('policyException/get', id)
        if (data) resetForm(data)
      } catch (_) {}
    }

    watch(
      () => currentRoute.value.params.id,
      (id) => {
        if (id) loadEditData()
      }
    )

    const onWorkflowAction = async (action) => {
      const id = currentRoute.value.params.id
      if (!id) return
      workflowLoading.value = true
      loading.value = true
      try {
        const payload = { status: action }
        await store.dispatch('policyException/updateStatus', { id, payload })
        toast({
          component: ToastificationContent,
          props: { title: 'Success', icon: 'CheckIcon', variant: 'success' },
        })
        await refetchCurrentItem()
      } catch (error) {
        const errData = error && error.response && error.response.data ? error.response.data : {}
        toast({
          component: ToastificationContent,
          props: {
            title: (errData && errData.message) || 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        workflowLoading.value = false
        loading.value = false
      }
    }

    const onSendToReview = async () => {
      if (!selectedReviewer.value) {
        toast({
          component: ToastificationContent,
          props: {
            title: t('policy_exception.select_reviewer_first'),
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      const validation = validateBeforeReview()
      if (!validation.ok) {
        const missingNames = validation.missing.map((m) => m.name).join(', ')
        toast({
          component: ToastificationContent,
          props: {
            title: `Missing: ${missingNames}`,
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      const id = currentRoute.value.params.id
      if (!id) return
      workflowLoading.value = true
      loading.value = true
      try {
        const payload = { status: 'in_review', id_user: selectedReviewer.value }
        await store.dispatch('policyException/updateStatus', { id, payload })
        selectedReviewer.value = null
        toast({
          component: ToastificationContent,
          props: { title: 'Sent to review', icon: 'CheckIcon', variant: 'success' },
        })
        await refetchCurrentItem()
      } catch (error) {
        const errData = error && error.response && error.response.data ? error.response.data : {}
        toast({
          component: ToastificationContent,
          props: {
            title: (errData && errData.message) || 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        workflowLoading.value = false
        loading.value = false
      }
    }

    const onReassign = async () => {
      if (!selectedReassign.value) return
      const id = currentRoute.value.params.id
      if (!id) return
      workflowLoading.value = true
      loading.value = true
      try {
        const payload = { status: 'reassigned', id_user: selectedReassign.value }
        await store.dispatch('policyException/updateStatus', { id, payload })
        selectedReassign.value = null
        toast({
          component: ToastificationContent,
          props: { title: 'Reassigned', icon: 'CheckIcon', variant: 'success' },
        })
        await refetchCurrentItem()
      } catch (error) {
        const errData = error && error.response && error.response.data ? error.response.data : {}
        toast({
          component: ToastificationContent,
          props: {
            title: (errData && errData.message) || 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        workflowLoading.value = false
        loading.value = false
      }
    }

    const scopeTypeOptions = computed(() => catalogsRef.value.scope_types || [])
    const flattenPolicyExceptionStandards = (list) => {
      return (list || []).map((item) => {
        const nested = item.standard
        const id = Number(item.standard_id ?? nested?.id ?? item.id)
        return nested ? { ...nested, id } : { ...item, id: Number(item.id) }
      })
    }
    const standardOptions = computed(() => {
      const catalogList = catalogsRef.value.standards || []
      const rawItemStandards = (currentItem.value && currentItem.value.standards) || []
      const itemStandards = flattenPolicyExceptionStandards(rawItemStandards)
      const merged = new Map()
      ;[...itemStandards, ...catalogList].forEach((s) => {
        const id = Number(s.id)
        if (!merged.has(id)) {
          merged.set(id, {
            ...s,
            id,
            label: [s.name, s.code].filter(Boolean).join(' | ') || String(s.id),
          })
        }
      })
      const selectedIds = formData.value.standard_ids || []
      selectedIds.forEach((rawId) => {
        const id = Number(rawId)
        if (!Number.isNaN(id) && !merged.has(id)) {
          merged.set(id, { id, label: `Standard #${id}` })
        }
      })
      return Array.from(merged.values())
    })
    const filterStandardByNameOrCode = (option, label, search) => {
      if (!search) return true
      const s = String(search).toLowerCase()
      const name = String(option?.name || '').toLowerCase()
      const code = String(option?.code || '').toLowerCase()
      return name.includes(s) || code.includes(s)
    }
    const formatOptionLabel = (name) => String(name || '').replace(/_/g, ' ')
    const formatSourceDisplayName = (name) => {
      const raw = String(name || '').trim()
      const normalized = raw.replace(/_/g, ' ').toLowerCase().replace(/\s+/g, ' ')
      if (normalized === 'analisis riesgo') return 'Análisis de Riesgo'
      if (normalized === 'proceso bau') return 'Proceso BAU'
      const withSpaces = raw.replace(/_/g, ' ')
      return withSpaces
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    }
    const requestTypeOptions = computed(() => catalogsRef.value.request_types || [])
    const isRequestTypeLocked = computed(() => true)
    const isInProgressStatus = computed(() => getStatusName(currentItem.value) === 'in_progress')
    const isRequestDateLocked = computed(() => isEditMode.value)
    const findNuevoRequestTypeId = (options) => {
      if (!Array.isArray(options) || options.length === 0) return null
      const found =
        options.find(
          (r) =>
            String(r.name || '').toLowerCase().includes('nuevo') ||
            String(r.name || '').toLowerCase() === 'new'
        ) || options[0]
      return found && (found.id != null || found.value != null) ? (found.id ?? found.value) : null
    }
    watch(
      () => {
        const opts = requestTypeOptions && requestTypeOptions.value !== undefined ? requestTypeOptions.value : []
        const isEdit = isEditMode && isEditMode.value !== undefined ? isEditMode.value : false
        return [Array.isArray(opts) ? opts : [], !!isEdit]
      },
      ([options, isEdit]) => {
        if (!isEdit && Array.isArray(options) && options.length) {
          const nuevoId = findNuevoRequestTypeId(options)
          if (nuevoId != null) formData.value.request_type_id = nuevoId
        }
      },
      { immediate: true }
    )
    const exceptionTypeOptions = computed(() =>
      (catalogsRef.value.exception_types || []).map((opt) => ({ ...opt, name: formatOptionLabel(opt.name) }))
    )
    const statusOptions = computed(() => catalogsRef.value.statuses || [])
    const sourceOptions = computed(() =>
      (catalogsRef.value.sources || []).map((opt) => ({ ...opt, name: formatSourceDisplayName(opt.name) }))
    )
    const inherentRiskOptions = computed(() => catalogsRef.value.inherent_risks || [])
    const residualRiskOptions = computed(() => catalogsRef.value.residual_risks || [])

    const requestDateConfig = computed(() => ({
      enableTime: false,
      dateFormat: 'Y-m-d',
      defaultDate: isEditMode.value ? null : new Date(),
      locale: { firstDayOfWeek: 0 },
    }))
    const startDateConfig = computed(() => {
      const requestDate = formData.value.request_date
      const minDate = requestDate ? new Date(requestDate) : new Date()
      return {
        enableTime: false,
        dateFormat: 'Y-m-d',
        minDate,
        locale: { firstDayOfWeek: 0 },
      }
    })
    const endDateConfig = computed(() => {
      const requestDate = formData.value.request_date
      if (!requestDate) return { enableTime: false, dateFormat: 'Y-m-d', locale: { firstDayOfWeek: 0 } }
      const req = new Date(String(requestDate).slice(0, 10))
      const dayAfter = new Date(req)
      dayAfter.setDate(dayAfter.getDate() + 1)
      return {
        enableTime: false,
        dateFormat: 'Y-m-d',
        minDate: dayAfter,
        locale: { firstDayOfWeek: 0 },
      }
    })

    const tenantStorageParams = {
      name: '',
      per_page: 10,
      'page[n]': 1,
      order_by: 'id',
      order_dir: true,
      assigned_space_min: 0,
      assigned_space_max: 0,
      used_space_min: 0,
      used_space_max: 0,
    }

    onMounted(async () => {
      loading.value = true
      store.dispatch('tenants_storage/getAll', tenantStorageParams)

      if (isEditMode.value) {
        loadError.value = false
        try {
          await loadInitialEditData()
        } catch (_) {
          loadError.value = true
        } finally {
          loading.value = false
        }
      } else {
        try {
          const [catalogsPayload, standards, reviewers, reassignable] = await Promise.all([
            store.dispatch('policyException/fetchCatalogs'),
            store.dispatch('policyException/fetchStandards'),
            fetchReviewerOptions(),
            fetchReassignableOptions(),
          ])
          catalogsRef.value = {
            ...(catalogsPayload || store.getters['policyException/getCatalogs'] || {}),
            standards: Array.isArray(standards) && standards.length ? standards : (catalogsPayload?.standards || []),
          }
          userOptions.value = Array.isArray(reviewers) ? reviewers : []
          reassignableOptions.value = Array.isArray(reassignable) ? reassignable : []
          const types = catalogsRef.value.request_types || []
          const nuevoId = findNuevoRequestTypeId(types)
          if (nuevoId != null) formData.value.request_type_id = nuevoId
        } catch (_) {
          catalogsRef.value = store.getters['policyException/getCatalogs'] || {}
        } finally {
          loading.value = false
        }
      }
    })

    const onSubmit = async (options = {}) => {
      const validateFull = options.validateFull === true

      if (!validateFull) {
        const saveValidation = validateForSave()
        if (!saveValidation.ok) {
          const missingCount = saveValidation.missing.length
          const lastMissing = saveValidation.missing[missingCount - 1]
          const lastFieldName = lastMissing ? t(getFieldLabelKey(lastMissing.name)) : ''
          const message =
            missingCount === 1
              ? `${t('policy_exception.one_required_field_missing')}: ${lastFieldName}`
              : `${t('policy_exception.multiple_required_fields_missing', { count: missingCount })}. ${t('policy_exception.last_field')}: ${lastFieldName}`
          toast({
            component: ToastificationContent,
            props: {
              title: message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          const errors = {}
          saveValidation.missing.forEach((m) => {
            const fieldName = m.name === 'scope_type' ? 'scope_type_id' : m.name
            errors[fieldName] = [message]
          })
          refFormObserver.value?.setErrors(errors)
          return false
        }
      } else {
        dateValidationErrors.value = validateDates()
        if (dateValidationErrors.value.length) {
          refFormObserver.value?.validate()
          const firstError = dateValidationErrors.value[0]
          const message = firstError && firstError.message ? t(`policy_exception.${firstError.message}`) : t('policy_exception.one_required_field_missing')
          toast({
            component: ToastificationContent,
            props: {
              title: message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return false
        }
        const validation = validateBeforeReview()
        if (!validation.ok) {
          refFormObserver.value?.validate()
          const missingCount = validation.missing.length
          const lastMissing = validation.missing[missingCount - 1]
          const lastFieldName = lastMissing ? t(getFieldLabelKey(lastMissing.name)) : ''
          const message =
            missingCount === 1
              ? `${t('policy_exception.one_required_field_missing')}: ${lastFieldName}`
              : `${t('policy_exception.multiple_required_fields_missing', { count: missingCount })}. ${t('policy_exception.last_field')}: ${lastFieldName}`
          toast({
            component: ToastificationContent,
            props: {
              title: message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return false
        }
      }

      isSubmitting.value = true
      try {
        const inProgressId = resolveInProgressStatusId(catalogsRef.value)
        const effectiveStatusId = isEditMode.value ? formData.value.status_id : inProgressId
        const existingAttachments = (formData.value.attachments || []).filter((a) => a.url || a.name).map((a) => ({ url: a.url || '', name: a.name || '' }))
        const newFiles = (formData.value.attachments || []).filter((a) => a.raw && a.raw instanceof File).map((a) => a.raw)
        const existingSectionAttachments = (formData.value.section_attachments || []).filter((a) => a.url || a.name).map((a) => ({ url: a.url || '', name: a.name || '' }))
        const newSectionFiles = (formData.value.section_attachments || []).filter((a) => a.raw && a.raw instanceof File).map((a) => a.raw)
        const useFormData = newFiles.length > 0 || newSectionFiles.length > 0
        const payload = {
          reference_number: formData.value.reference_number,
          scope_type_id: formData.value.scope_type_id,
          request_type_id: formData.value.request_type_id,
          exception_type_id: formData.value.exception_type_id,
          ...(effectiveStatusId != null && { status_id: effectiveStatusId }),
          source_id: formData.value.source_id,
          inherent_risk_id: formData.value.inherent_risk_id,
          residual_risk_id: formData.value.residual_risk_id,
          standard_ids: formData.value.standard_ids && formData.value.standard_ids.length ? formData.value.standard_ids : [],
          regulatory_required: formData.value.regulatory_required,
          ...(formData.value.regulatory_required && { regulation_description: formData.value.regulation_description }),
          ...(formData.value.description && { description: formData.value.description }),
          ...(formData.value.compensatory_measures && { compensatory_measures: formData.value.compensatory_measures }),
          ...(formData.value.justification && { justification: formData.value.justification }),
          ...(formData.value.section_detail && { section_detail: formData.value.section_detail }),
          request_date: formData.value.request_date,
          start_date: formData.value.start_date,
          end_date: formData.value.end_date,
          ...((formData.value.unit_ids && formData.value.unit_ids.length) && { unit_ids: formData.value.unit_ids }),
          ...((formData.value.sector_ids && formData.value.sector_ids.length) && { sector_ids: formData.value.sector_ids }),
          ...((existingAttachments.length > 0 || newFiles.length > 0) && { attachments: existingAttachments }),
          ...((existingSectionAttachments.length > 0 || newSectionFiles.length > 0) && { section_attachments: existingSectionAttachments }),
          ...(isEditMode.value && { comments: formData.value.comments != null ? formData.value.comments : '' }),
        }
        let response
        if (useFormData) {
          const fd = buildFormDataFromPayload(payload, newFiles, newSectionFiles)
          if (isEditMode.value) {
            response = await store.dispatch('policyException/patchWithFormData', { id: currentRoute.value.params.id, formData: fd })
          } else {
            response = await store.dispatch('policyException/addWithFormData', { formData: fd })
          }
        } else {
          const jsonPayload = { ...payload }
          if (!jsonPayload.attachments || jsonPayload.attachments.length === 0) delete jsonPayload.attachments
          if (!jsonPayload.section_attachments || jsonPayload.section_attachments.length === 0) delete jsonPayload.section_attachments
          if (isEditMode.value) {
            response = await store.dispatch('policyException/patch', { id: currentRoute.value.params.id, payload: jsonPayload })
          } else {
            response = await store.dispatch('policyException/add', jsonPayload)
          }
        }
        const id = isEditMode.value ? currentRoute.value.params.id : ((response && response.data && response.data.id) || (response && response.id))
        toast({
          component: ToastificationContent,
          props: {
            title: isEditMode.value ? 'Policy exception updated' : 'Policy exception created',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        if (isEditMode.value) {
          await refetchCurrentItem()
        } else {
          root.$router.push({ name: 'policyExceptionEdit', params: { id } })
        }
        return true
      } catch (error) {
        const errData = error && error.response && error.response.data ? error.response.data : {}
        if (errData.errors) {
          refFormObserver.value.setErrors(errData.errors)
          return false
        }
        const isConflictInmuebles =
          errData.message === 'policy_exception.conflict_inmuebles_in_progress' &&
          errData.data &&
          Array.isArray(errData.data.conflicting_policies)
        if (isConflictInmuebles) {
          const conflictMessage = buildConflictInmueblesMessage(errData.data.conflicting_policies)
          toast({
            component: ToastificationContent,
            props: {
              title: t('policy_exception.conflict_inmuebles_in_progress'),
              text: conflictMessage,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return false
        }
        toast({
          component: ToastificationContent,
          props: {
            title: (errData && errData.message) || (isEditMode.value ? 'Error updating' : 'Error creating policy exception'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return false
      } finally {
        isSubmitting.value = false
      }
    }

    const runSaveThenWorkflow = async (workflowAction) => {
      workflowLoading.value = true
      try {
        const saveOk = await onSubmit({ validateFull: true })
        if (!saveOk) return
        await workflowAction()
      } finally {
        workflowLoading.value = false
      }
    }

    const goBack = () => root.$router.push({ name: 'policyExceptions' })

    return {
      formData,
      unitOptions,
      sectorOptions,
      unitInitialOptions,
      sectorInitialOptions,
      scopeTypeOptions,
      standardOptions,
      filterStandardByNameOrCode,
      requestTypeOptions,
      exceptionTypeOptions,
      statusOptions,
      sourceOptions,
      inherentRiskOptions,
      residualRiskOptions,
      requestDateConfig,
      startDateConfig,
      endDateConfig,
      showUnitsSelector,
      showSectorsSelector,
      showRegulationDescription,
      isSectionDetailEnabled,
      showStandardsAffected,
      refFormObserver,
      isSubmitting,
      isEditMode,
      loading,
      loadError,
      onSubmit,
      runSaveThenWorkflow,
      goBack,
      currentItem,
      selectedReviewer,
      selectedReassign,
      userOptions,
      reassignableOptions,
      workflowLoading,
      canEditFormItem,
      canSendToReviewItem,
      canReviewItem,
      canReassignAfterRejectedItem,
      isCurrentUserTheRejectorItem,
      showReassignAsNonAssigneeItem,
      showSpecialButtonsBlockItem,
      statusAllowsSendToReview,
      isReassignInReviewMode,
      canAssignNewUser,
      showReviewActions,
      sortedStatusHistory,
      historyGridStyle,
      riskLevelTableData,
      formatHistoryDate,
      getHistoryDate,
      getStatusNameFromEntry,
      getUserNameFromEntry,
      getUserEmailFromEntry,
      onWorkflowAction,
      onSendToReview,
      onReassign,
      attachmentsInputRef,
      sectionAttachmentsInputRef,
      handleAttachmentChange,
      removeAttachment,
      onAttachmentClick,
      handleSectionAttachmentChange,
      removeSectionAttachment,
      onSectionAttachmentClick,
      showRiskTableModal,
      isRequestTypeLocked,
      isInProgressStatus,
      isRequestDateLocked,
    }
  },
}
</script>

<style scoped>
.policy-exception-special-select {
  width: 600px;
  min-width: 600px;
}
.policy-exception-loading-overlay {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  margin: 0.5rem;
}
.risk-level-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}
.risk-level-table {
  width: 100%;
}
.risk-level-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
  align-items: start;
}
.risk-level-row:last-child {
  border-bottom: 0;
}
.risk-level-row--head {
  font-weight: 600;
  color: #444;
  padding-bottom: 8px;
}
.risk-level-col-level {
  min-width: 100px;
}
.risk-level-col-desc {
  white-space: normal;
  text-transform: none !important;
}
.risk-level-desc {
  font-weight: normal;
  color: inherit;
}
.history-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}
.history-header {
  margin-bottom: 8px;
}
.history-table {
  width: 100%;
}
.history-row {
  display: grid;
  grid-column-gap: 8px;
  column-gap: 8px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}
.history-row:last-child {
  border-bottom: 0;
}
.history-row--head {
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #444;
  padding-top: 0;
  padding-bottom: 8px;
}
.col-state {
  justify-self: start;
}
.col-user {
  justify-self: start;
  min-width: 0;
}
.col-date {
  justify-self: end;
  white-space: nowrap;
  overflow: visible;
}
.history-row--head > div,
.history-row > .col-user {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-date {
  font-size: 12px;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

.standards-select.vs--disabled ::v-deep .vs__selected,
.standards-select.vs--disabled ::v-deep .vs__dropdown-toggle,
.standards-select.vs--disabled ::v-deep .vs__selected-options {
  color: #495057 !important;
  opacity: 1 !important;
}

.standards-select.vs--disabled ::v-deep .vs__deselect {
  opacity: 0.6;
}

.units-select ::v-deep .vs--disabled .vs__selected,
.units-select ::v-deep .vs--disabled .vs__dropdown-toggle,
.units-select ::v-deep .vs--disabled .vs__selected-options {
  color: #495057 !important;
  opacity: 1 !important;
}

.units-select ::v-deep .vs--disabled .vs__deselect {
  opacity: 0.6;
}

.sectors-select ::v-deep .vs--disabled .vs__selected,
.sectors-select ::v-deep .vs--disabled .vs__dropdown-toggle,
.sectors-select ::v-deep .vs--disabled .vs__selected-options {
  color: #495057 !important;
  opacity: 1 !important;
}

.sectors-select ::v-deep .vs--disabled .vs__deselect {
  opacity: 0.6;
}

.goback-text {
  position: relative;
  top: 0.1rem;
  margin-left: 0.5rem !important;
}

.attachments-upload-btn {
  position: relative;
  overflow: hidden;
}

.file-input-hidden {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
}

.attachments-info.file-upload-info-box {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 12px;
  background: #e0f2fe;
  border-radius: 6px;
}

.attachment-preview.cursor-pointer {
  cursor: pointer;
  pointer-events: auto;
}

.attachments-info .file-upload-info-text {
  color: #0c4a6e;
  font-size: 0.875rem;
}

.attachments-info .info-icon {
  flex-shrink: 0;
  color: #0369a1;
}

.attachment-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.attachment-name {
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
